<script setup>
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';

  const { t } = useI18n();
  const i18nScope = 'components.sales_analytics';

  const props = defineProps({
    libraryData: {
      type: Array,
      default: () => []
    },
    peerData: {
      type: Array,
      default: () => []
    },
    libraryName: {
      type: String,
      default: 'Your Library'
    },
    peerLabel: {
      type: String,
      default: 'Peer Average'
    }
  });

  // Merge library and peer data by month
  const chartData = computed(() => {
    const monthMap = new Map();

    // Add library data
    props.libraryData.forEach(item => {
      monthMap.set(item.month, {
        month: item.month,
        libraryCount: item.count,
        librarySpend: item.spend,
        peerCount: 0,
        peerSpend: 0
      });
    });

    // Add peer data
    if (props.peerData) {
      props.peerData.forEach(item => {
        if (monthMap.has(item.month)) {
          const existing = monthMap.get(item.month);
          existing.peerCount = item.count;
          existing.peerSpend = item.spend;
        } else {
          monthMap.set(item.month, {
            month: item.month,
            libraryCount: 0,
            librarySpend: 0,
            peerCount: item.count,
            peerSpend: item.spend
          });
        }
      });
    }

    // Sort by month and return array
    return Array.from(monthMap.values()).sort((a, b) => a.month.localeCompare(b.month));
  });

  const maxCount = computed(() => {
    if (chartData.value.length === 0) return 1;
    return Math.max(
      ...chartData.value.map(d => Math.max(d.libraryCount, d.peerCount))
    );
  });

  function getBarHeight(count) {
    return (count / maxCount.value) * 100;
  }

  function formatMonth(monthStr) {
    const [year, month] = monthStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  }
</script>

<template>
  <div class="time-series-chart">
    <div v-if="chartData.length === 0" class="text-center text-muted py-4">
      {{ t(`${i18nScope}.no_data`) }}
    </div>

    <div v-else>
      <!-- Legend -->
      <div class="chart-legend mb-3">
        <span class="legend-item">
          <span class="legend-color library-color"></span>
          {{ libraryName }}
        </span>
        <span v-if="peerData?.length" class="legend-item">
          <span class="legend-color peer-color"></span>
          {{ peerLabel }}
        </span>
      </div>

      <!-- Chart -->
      <div class="chart-container">
        <div class="chart-bars">
          <div v-for="item in chartData" :key="item.month" class="bar-group">
            <div class="bars">
              <div
                class="bar library-bar"
                :style="{ height: getBarHeight(item.libraryCount) + '%' }"
                data-bs-toggle="tooltip" :data-bs-title="`${libraryName}: ${item.libraryCount}`"
              >
                <span class="bar-value" v-if="item.libraryCount > 0">{{ item.libraryCount }}</span>
              </div>
              <div
                v-if="peerData?.length"
                class="bar peer-bar"
                :style="{ height: getBarHeight(item.peerCount) + '%' }"
                data-bs-toggle="tooltip" :data-bs-title="`${peerLabel}: ${item.peerCount}`"
              >
                <span class="bar-value" v-if="item.peerCount > 0">{{ item.peerCount }}</span>
              </div>
            </div>
            <div class="bar-label">{{ formatMonth(item.month) }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .time-series-chart {
    min-height: 250px;
  }

  .chart-legend {
    display: flex;
    gap: 20px;
    font-size: 0.875rem;
  }

  .legend-item {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .legend-color {
    width: 16px;
    height: 16px;
    border-radius: 3px;
  }

  .library-color {
    background-color: #0d6efd;
  }

  .peer-color {
    background-color: #6c757d;
  }

  .chart-container {
    overflow-x: auto;
  }

  .chart-bars {
    display: flex;
    align-items: flex-end;
    height: 200px;
    gap: 4px;
    min-width: max-content;
    padding-bottom: 30px;
    position: relative;
  }

  .bar-group {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 50px;
  }

  .bars {
    display: flex;
    align-items: flex-end;
    gap: 2px;
    height: 180px;
  }

  .bar {
    width: 20px;
    border-radius: 3px 3px 0 0;
    transition: height 0.3s ease;
    position: relative;
    min-height: 2px;
  }

  .library-bar {
    background-color: #0d6efd;
  }

  .peer-bar {
    background-color: #6c757d;
  }

  .bar-value {
    position: absolute;
    top: -20px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 0.7rem;
    color: #666;
    white-space: nowrap;
  }

  .bar-label {
    font-size: 0.75rem;
    color: #666;
    margin-top: 8px;
    white-space: nowrap;
  }
</style>
